/**
 * Review Prompt Service
 *
 * Two-step in-app review flow:
 *   1. We gate on engagement signals (opens + positive action + onboarding
 *      cooldown) and platform (iOS native only).
 *   2. When gates pass, the UI opens our friendly "Are you enjoying TeeSync?"
 *      modal (ReviewPromptModal). The native SKStoreReviewController call
 *      (via @capacitor-community/in-app-review) only fires when the user
 *      taps Yes, so we always have a clear engagement signal.
 *
 * The native call is exposed here so the modal can `try/catch` and surface
 * a visible error if the plugin isn't present (it shouldn't fail in
 * practice — it's installed and linked at ios/App/CapApp-SPM/Package.swift).
 *
 * Apple caps SKStoreReviewController to ~3 sheets per user per year per
 * app and may silently no-op on rate-limit. Our 90-day local cooldown on
 * top of Apple's cap keeps us from re-asking too eagerly.
 *
 * Storage keys (Capacitor Preferences on native, localStorage on web):
 *   review_app_open_count       - integer string
 *   review_action_<name>        - 'true' once a positive action has fired
 *   review_prompted_at          - ISO timestamp of the most recent prompt
 *   review_prompt_outcome       - 'yes' | 'no' | 'dismissed'
 *   review_onboarding_done_at   - ISO timestamp; used for the 24h cooldown
 */

import { getItem, setItem } from './storageService';
import { isNative, isIOS } from './platformService';

const KEY_OPEN_COUNT = 'review_app_open_count';
const KEY_PROMPTED_AT = 'review_prompted_at';
const KEY_PROMPT_OUTCOME = 'review_prompt_outcome';
const KEY_ONBOARDING_DONE_AT = 'review_onboarding_done_at';
const ACTION_KEY = (name) => `review_action_${name}`;

const POSITIVE_ACTIONS = ['onboarding_complete', 'invite_accepted', 'round_scheduled'];
const MIN_OPENS = 3;
const ONBOARDING_COOLDOWN_MS = 24 * 60 * 60 * 1000;
// 90-day cooldown after a "no" or "dismissed" outcome. "yes" never re-asks
// — they already did the thing. This replaces the previous once-per-install
// lock which silently trapped users when Apple's SKStoreReviewController
// rate-limited their first sheet.
const RE_PROMPT_COOLDOWN_MS = 90 * 24 * 60 * 60 * 1000;

export const recordAppOpen = async () => {
  try {
    const raw = await getItem(KEY_OPEN_COUNT);
    const next = (parseInt(raw, 10) || 0) + 1;
    await setItem(KEY_OPEN_COUNT, String(next));
  } catch (err) {
    console.warn('[reviewPrompt] recordAppOpen failed:', err);
  }
};

export const recordPositiveAction = async (action) => {
  if (!POSITIVE_ACTIONS.includes(action)) return;
  try {
    await setItem(ACTION_KEY(action), 'true');
    if (action === 'onboarding_complete') {
      await setItem(KEY_ONBOARDING_DONE_AT, new Date().toISOString());
    }
  } catch (err) {
    console.warn('[reviewPrompt] recordPositiveAction failed:', err);
  }
};

/**
 * Returns true when all gates pass and the friendly modal should open.
 * Replaces the older `gatesPass()` private helper — exposed so the
 * useNativeInit hook can drive the modal, instead of the service calling
 * the native API directly.
 */
export const shouldPromptReview = async () => {
  if (!isNative() || !isIOS()) return false;

  const promptedAt = await getItem(KEY_PROMPTED_AT);
  const outcome = await getItem(KEY_PROMPT_OUTCOME);
  if (promptedAt) {
    if (outcome === 'yes') return false;
    const elapsed = Date.now() - new Date(promptedAt).getTime();
    if (elapsed < RE_PROMPT_COOLDOWN_MS) return false;
  }

  const opens = parseInt(await getItem(KEY_OPEN_COUNT), 10) || 0;
  if (opens < MIN_OPENS) return false;

  const actionFlags = await Promise.all(POSITIVE_ACTIONS.map((a) => getItem(ACTION_KEY(a))));
  if (!actionFlags.some((v) => v === 'true')) return false;

  const onboardingDoneAt = await getItem(KEY_ONBOARDING_DONE_AT);
  if (onboardingDoneAt) {
    const elapsed = Date.now() - new Date(onboardingDoneAt).getTime();
    if (elapsed < ONBOARDING_COOLDOWN_MS) return false;
  }

  return true;
};

/**
 * Records the user's response to the friendly modal. Persists `outcome`
 * and stamps `review_prompted_at` so we honour the 90-day cooldown.
 */
export const recordPromptOutcome = async (outcome) => {
  if (!['yes', 'no', 'dismissed'].includes(outcome)) return;
  try {
    await setItem(KEY_PROMPT_OUTCOME, outcome);
    await setItem(KEY_PROMPTED_AT, new Date().toISOString());
  } catch (err) {
    console.warn('[reviewPrompt] recordPromptOutcome failed:', err);
  }
};

/**
 * Fires Apple's SKStoreReviewController via the @capacitor-community
 * /in-app-review plugin. Throws if the plugin can't be loaded or the
 * call rejects so the caller (the modal) can surface a visible error.
 *
 * Note: even on success, Apple may silently skip showing the sheet
 * because of its rate-limit (~3 per user per year per app) — there is
 * no observable signal from the API when iOS declines to show it.
 */
export const requestNativeReview = async () => {
  // Static-string dynamic import so Vite bundles the plugin for the native
  // iOS build. The previous /* @vite-ignore */ + variable-indirection form
  // skipped bundling, so on iOS the import resolved to a missing module URL
  // and always hit the catch in ReviewPromptModal (the "Could not open
  // the review sheet" toast).
  const { InAppReview } = await import('@capacitor-community/in-app-review');
  if (!InAppReview?.requestReview) {
    throw new Error('in-app-review plugin is not available');
  }
  await InAppReview.requestReview();
};

/**
 * Convenience helper for the legacy direct-fire path. Kept so existing
 * callers don't break, but the new two-step flow goes through the
 * `shouldPromptReview()` → modal → `requestNativeReview()` path instead.
 */
export const maybeRequestReview = async () => {
  try {
    if (!(await shouldPromptReview())) return false;
    await requestNativeReview();
    await recordPromptOutcome('yes');
    return true;
  } catch (err) {
    console.warn('[reviewPrompt] maybeRequestReview failed:', err);
    return false;
  }
};

export default {
  recordAppOpen,
  recordPositiveAction,
  shouldPromptReview,
  recordPromptOutcome,
  requestNativeReview,
  maybeRequestReview,
};
